import { c as _c } from "react/compiler-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import LogoItzGreyscale from 'assets/images/logos/itz-greyscale.svg';
import OrgaLogo from 'assets/images/logos/orga-logo.svg';
import classNames from 'classnames';
import { type ReactNode, useState } from 'react';
import { Icon } from '../../components/Icon';
import { LayoutAccordion, LayoutAccordionItem } from '../../components/layouts/LayoutAccordion';
import { LayoutCenter } from '../../components/layouts/LayoutCenter';
import { LayoutCluster } from '../../components/layouts/LayoutCluster';
import { LayoutStack } from '../../components/layouts/LayoutStack';
import { headlessLinkProps } from '../../components/TextLink';
import useIsMounted from '../../hooks/useIsMounted';
import { getSafeImageSource } from '../../utils';
import { DefaultImage, DefaultLink, getDomain, LinkItem, type LinkItemProps } from '../shared';
import styles from './Footer.module.css';
import TaxReceiptHint from './TaxReceiptHint';
import type { FooterProps, FooterText, Section } from './types';
const orgaLogo = getSafeImageSource(OrgaLogo);
const logoItz = getSafeImageSource(LogoItzGreyscale);
const onCookieSettingsLinkClick = () => {
  const domain = getDomain();
  const maxAge = new Date(2099, 1, 1).getTime();
  document.cookie = `betterplace-tracking-accepted=edit; path=/; max-age=${maxAge}; domain=.${domain}`;
};
function mapSections(sections: Section[]) {
  if (!sections.length) return [];
  const sorted = sections.length > 1 ? sections.sort(({
    column: a
  }, {
    column: b
  }) => a - b) : sections;
  let column = 0;
  return sorted.map(section => {
    column = section.column ?? column;
    const res = {
      ...section,
      column: section.column ?? column
    };
    column++;
    return res;
  });
}
function groupByColumn(sections: Section[]) {
  const grouped = sections.reduce((acc, section) => {
    acc[section.column] = [...(acc[section.column] ?? []), section];
    return acc;
  }, {} as Record<number, Section[]>);
  return Object.values(grouped);
}
function FooterLinkItem(t0) {
  const $ = _c(6);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    t1 = headlessLinkProps({
      kind: "secondary",
      size: "small",
      className: classNames(props.className, styles.footerLink)
    });
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const {
    className
  } = t1;
  let t2;
  if ($[3] !== className || $[4] !== props) {
    t2 = <LinkItem {...props} doNotOpenInNewTab={true} className={className} />;
    $[3] = className;
    $[4] = props;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function CookieSettingsLink(t0) {
  const $ = _c(3);
  const {
    cookieSettingsLink,
    Link: t1,
    section
  } = t0;
  const Link = t1 === undefined ? DefaultLink : t1;
  if (section.title !== cookieSettingsLink.section) {
    return null;
  }
  let t2;
  if ($[0] !== Link || $[1] !== cookieSettingsLink.label) {
    t2 = <FooterLinkItem key="cookie" Link={Link} onClick={onCookieSettingsLinkClick} className={styles.cookieSettingsLink}>{cookieSettingsLink.label}</FooterLinkItem>;
    $[0] = Link;
    $[1] = cookieSettingsLink.label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function FooterMobileSectionContent(t0) {
  const $ = _c(8);
  const {
    children,
    Link: t1,
    links,
    mobile
  } = t0;
  const Link = t1 === undefined ? DefaultLink : t1;
  if (mobile) {
    return mobile;
  }
  let t2;
  if ($[0] !== Link || $[1] !== links) {
    let t3;
    if ($[3] !== Link) {
      t3 = (linkItemProps, index) => <FooterLinkItem key={index} Link={Link} {...linkItemProps} />;
      $[3] = Link;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t2 = links.map(t3);
    $[0] = Link;
    $[1] = links;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[5] !== children || $[6] !== t2) {
    t3 = <LayoutStack space="200" className={styles.accordionContent}>{t2}{children}</LayoutStack>;
    $[5] = children;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
export function FooterDesktopSectionContent(t0) {
  const $ = _c(8);
  const {
    links,
    Link: t1,
    desktop,
    children
  } = t0;
  const Link = t1 === undefined ? DefaultLink : t1;
  if (desktop) {
    return desktop;
  }
  let t2;
  if ($[0] !== Link || $[1] !== links) {
    let t3;
    if ($[3] !== Link) {
      t3 = (linkItemProps, index) => <FooterLinkItem key={index} Link={Link} {...linkItemProps} />;
      $[3] = Link;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t2 = links.map(t3);
    $[0] = Link;
    $[1] = links;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[5] !== children || $[6] !== t2) {
    t3 = <>{t2}{children}</>;
    $[5] = children;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function DesktopFooter(t0) {
  const $ = _c(25);
  const {
    text,
    Link: t1
  } = t0;
  const Link = t1 === undefined ? DefaultLink : t1;
  let T0;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== Link || $[1] !== text.cookieSettingsLink || $[2] !== text.sections) {
    const groups = groupByColumn(mapSections(text.sections));
    t8 = styles.wideOnly;
    T0 = LayoutCluster;
    t2 = styles.grid;
    t3 = true;
    t4 = "300";
    t5 = "space-between";
    t6 = true;
    let t9;
    if ($[11] !== Link || $[12] !== text.cookieSettingsLink) {
      t9 = (group, index) => <LayoutStack key={index} className={styles.item} space="300">{group.map(section => <LayoutStack key={section.title}><p key="title"><strong>{section.title}</strong></p><FooterDesktopSectionContent links={section.links} desktop={section.desktop} Link={Link}><CookieSettingsLink key="cookie" cookieSettingsLink={text.cookieSettingsLink} section={section} /></FooterDesktopSectionContent></LayoutStack>)}</LayoutStack>;
      $[11] = Link;
      $[12] = text.cookieSettingsLink;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    t7 = groups.map(t9);
    $[0] = Link;
    $[1] = text.cookieSettingsLink;
    $[2] = text.sections;
    $[3] = T0;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    T0 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
  }
  let t9;
  if ($[14] !== T0 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== t7) {
    t9 = <T0 className={t2} flex={t3} space={t4} justify={t5} wrap={t6}>{t7}</T0>;
    $[14] = T0;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t8 || $[23] !== t9) {
    t10 = <nav className={t8}>{t9}</nav>;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
}
function MobileFooter(t0) {
  const $ = _c(10);
  const {
    text,
    Link: t1
  } = t0;
  const Link = t1 === undefined ? DefaultLink : t1;
  const [selected, setSelected] = useState("");
  let t2;
  if ($[0] !== Link || $[1] !== text.cookieSettingsLink || $[2] !== text.sections) {
    let t3;
    if ($[4] !== Link || $[5] !== text.cookieSettingsLink) {
      t3 = section => <LayoutAccordionItem key={section.title} id={section.title} onClick={key => setSelected(prev => key === prev ? "" : key)} label={<span className={styles.accordionLabel}>{section.title}</span>}><FooterMobileSectionContent links={section.links} mobile={section.mobile} Link={Link}><CookieSettingsLink key="cookie" cookieSettingsLink={text.cookieSettingsLink} section={section} /></FooterMobileSectionContent></LayoutAccordionItem>;
      $[4] = Link;
      $[5] = text.cookieSettingsLink;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = text.sections.map(t3);
    $[0] = Link;
    $[1] = text.cookieSettingsLink;
    $[2] = text.sections;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[7] !== selected || $[8] !== t2) {
    t3 = <nav className={styles.mobileOnly}><LayoutAccordion defaultKey="" activeKey={selected} scrollOnOpen={false} space="none" animationSpeedInMs={300}>{t2}</LayoutAccordion></nav>;
    $[7] = selected;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function useLocaleSwitchLink(t0) {
  const $ = _c(3);
  const {
    href,
    children
  } = t0;
  const mounted = useIsMounted();
  const t1 = !mounted || !href;
  const t2 = mounted && !children;
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = {
      disabled: t1,
      hidden: t2
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}
export function LocaleSwitchLink(t0) {
  const $ = _c(11);
  const {
    children,
    href,
    className,
    Link: t1
  } = t0;
  const Link = t1 === undefined ? DefaultLink : t1;
  let t2;
  if ($[0] !== children || $[1] !== href) {
    t2 = {
      href,
      children
    };
    $[0] = children;
    $[1] = href;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    disabled,
    hidden
  } = useLocaleSwitchLink(t2);
  if (hidden) {
    return null;
  }
  let t3;
  if ($[3] !== className) {
    t3 = classNames(styles.localeSwitchLink, className);
    $[3] = className;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== Link || $[6] !== children || $[7] !== disabled || $[8] !== href || $[9] !== t3) {
    t4 = <Link disabled={disabled} href={href} className={t3}>{children}</Link>;
    $[5] = Link;
    $[6] = children;
    $[7] = disabled;
    $[8] = href;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function Footer(t0) {
  const $ = _c(71);
  const {
    text,
    Link: t1,
    Image: t2,
    localeSwitchLink
  } = t0;
  const Link = t1 === undefined ? DefaultLink : t1;
  const Image = t2 === undefined ? DefaultImage : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = classNames("bp-shared", styles.footer);
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== Link || $[2] !== text.taxReceiptHint.children || $[3] !== text.taxReceiptHint.href || $[4] !== text.taxReceiptHint.label) {
    t4 = <TaxReceiptHint label={text.taxReceiptHint.label} href={text.taxReceiptHint.href} Link={Link}>{text.taxReceiptHint.children}</TaxReceiptHint>;
    $[1] = Link;
    $[2] = text.taxReceiptHint.children;
    $[3] = text.taxReceiptHint.href;
    $[4] = text.taxReceiptHint.label;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  let t6;
  if ($[6] !== Link || $[7] !== text) {
    t5 = <DesktopFooter text={text} Link={Link} />;
    t6 = <MobileFooter text={text} Link={Link} />;
    $[6] = Link;
    $[7] = text;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  let t7;
  if ($[10] !== Image) {
    t7 = <Image src={orgaLogo} alt="" width={190} className={styles.logo} />;
    $[10] = Image;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== text.organizationInfo) {
    t8 = <p><strong>{text.organizationInfo}</strong></p>;
    $[12] = text.organizationInfo;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== text.organizationInfoDetails) {
    t9 = <p className={styles.organizationInfoDetails}>{text.organizationInfoDetails}</p>;
    $[14] = text.organizationInfoDetails;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t8 || $[17] !== t9) {
    t10 = <LayoutStack className={styles.organizationInfo} flex={true}>{t8}{t9}</LayoutStack>;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Icon name="facebook" size="400" aria-hidden="true" />;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== text.facebookLink.label) {
    t12 = <span className="sr-only">{text.facebookLink.label}</span>;
    $[20] = text.facebookLink.label;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== Link || $[23] !== t12 || $[24] !== text.facebookLink.href || $[25] !== text.facebookLink.label) {
    t13 = <LinkItem key="facebook" Link={Link} href={text.facebookLink.href} title={text.facebookLink.label} external={true} className={styles.socialLink}>{t11}{t12}</LinkItem>;
    $[22] = Link;
    $[23] = t12;
    $[24] = text.facebookLink.href;
    $[25] = text.facebookLink.label;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Icon name="instagram" size="400" aria-hidden="true" />;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== text.instagramLink.label) {
    t15 = <span className="sr-only">{text.instagramLink.label}</span>;
    $[28] = text.instagramLink.label;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== Link || $[31] !== t15 || $[32] !== text.instagramLink.href || $[33] !== text.instagramLink.label) {
    t16 = <LinkItem key="instagram" Link={Link} href={text.instagramLink.href} title={text.instagramLink.label} external={true} className={styles.socialLink}>{t14}{t15}</LinkItem>;
    $[30] = Link;
    $[31] = t15;
    $[32] = text.instagramLink.href;
    $[33] = text.instagramLink.label;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Icon name="linkedin" size="400" aria-hidden="true" />;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== text.linkedinLink.label) {
    t18 = <span className="sr-only">{text.linkedinLink.label}</span>;
    $[36] = text.linkedinLink.label;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== Link || $[39] !== t18 || $[40] !== text.linkedinLink.href || $[41] !== text.linkedinLink.label) {
    t19 = <LinkItem key="linkedin" Link={Link} href={text.linkedinLink.href} title={text.linkedinLink.label} external={true} className={styles.socialLink}>{t17}{t18}</LinkItem>;
    $[38] = Link;
    $[39] = t18;
    $[40] = text.linkedinLink.href;
    $[41] = text.linkedinLink.label;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t13 || $[44] !== t16 || $[45] !== t19) {
    t20 = <LayoutCluster space="200" align="center">{t13}{t16}{t19}</LayoutCluster>;
    $[43] = t13;
    $[44] = t16;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== localeSwitchLink || $[48] !== t20) {
    t21 = <LayoutCluster justify="space-between" align="center">{t20}{localeSwitchLink}</LayoutCluster>;
    $[47] = localeSwitchLink;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== Image || $[51] !== text.transparentCivilSocietyLink.label) {
    t22 = <Image src={logoItz} alt={text.transparentCivilSocietyLink.label} width={240} />;
    $[50] = Image;
    $[51] = text.transparentCivilSocietyLink.label;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== Link || $[54] !== t22 || $[55] !== text.transparentCivilSocietyLink.href) {
    t23 = <LinkItem Link={Link} href={text.transparentCivilSocietyLink.href} className={styles.transparentCivilSocietyLink}>{t22}</LinkItem>;
    $[53] = Link;
    $[54] = t22;
    $[55] = text.transparentCivilSocietyLink.href;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== t21 || $[58] !== t23) {
    t24 = <LayoutStack className={styles.socialLinks}>{t21}{t23}</LayoutStack>;
    $[57] = t21;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t10 || $[61] !== t24) {
    t25 = <LayoutCluster alignment="intrinsic" justify="space-between" className={styles.textContent}>{t10}{t24}</LayoutCluster>;
    $[60] = t10;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== t25 || $[64] !== t5 || $[65] !== t6 || $[66] !== t7) {
    t26 = <LayoutStack className={styles.footerContent} flex={true}>{t5}{t6}{t7}{t25}</LayoutStack>;
    $[63] = t25;
    $[64] = t5;
    $[65] = t6;
    $[66] = t7;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== t26 || $[69] !== t4) {
    t27 = <LayoutCenter as="footer" className={t3} centeredChildren={true}>{t4}{t26}</LayoutCenter>;
    $[68] = t26;
    $[69] = t4;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  return t27;
}
export default Footer;